import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";
import BoiteOrga from "./BoiteOrga";

/**
 * Page d'arrivée depuis le logiciel métier Osmose.
 * Accessible uniquement depuis l'encart Osmose — non référencée (noindex).
 * Volontairement hors PublicShell : aucune navigation MetaConnect,
 * le professionnel ne doit pas sortir du parcours de souscription.
 */

const CONNEXION_URL = "https://ohmyvue.fr/login";

const POINTS = [
  {
    t: "Vous ouvrez sur une journée déjà organisée",
    d: "Vos créneaux sont publiés sur votre page. Le patient choisit sa prestation, son praticien et son horaire — un dimanche soir s'il le veut. Vous n'avez plus qu'à préparer les dossiers de la journée.",
    li: [
      "Réservation ouverte 24 h/24, y compris hors horaires",
      "Confirmation puis rappel envoyés au patient, sans vous",
      "Un agenda par professionnel rattaché, sans supplément",
      "Moins de créneaux perdus, moins de rendez-vous oubliés",
    ],
    link: { to: "/agenda-type", label: "Voir un agenda type, opticien et audio" },
    before: "Aujourd'hui : le patient qui appelle à 20 h tombe sur un répondeur.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 11h18" />
        <path d="M8.5 15.5l2 2 4-4" />
      </svg>
    ),
  },
  {
    t: "Le patient arrive avec son dossier déjà prêt",
    d: "Plus aucun document sur une boîte Gmail. Depuis la page de votre établissement, le patient téléverse lui-même ce dont vous avez besoin : ordonnance, carte de mutuelle, attestation de Sécurité sociale, certificats d'authenticité verres, monture ou lentilles. Les documents remontent dans Osmose, rattachés à sa fiche, avant qu'il pousse la porte.",
    li: [
      "Un dépôt sécurisé depuis votre page, pas une boîte grand public",
      "Ordonnance, mutuelle, attestation, certificats d'authenticité",
      "Identité vérifiée : le document ne se trompe pas de fiche",
      "Disponible dans Osmose, sans scan ni renommage",
    ],
    before: "Aujourd'hui : une photo par SMS, une pièce jointe sur Gmail, un scan à renommer.",
    link: {
      to: "/dossier-patient",
      label: "Voir ce que le patient prépare avant de venir",
    },
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14 3v5h5" />
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M12 17.5v-6M9.5 14l2.5-2.5L14.5 14" />
      </svg>
    ),
  },
  {
    t: "Partager avec l'ensemble des professionnels de santé n'a jamais été aussi simple",
    d: "Ophtalmologiste, orthoptiste, médecin traitant, hôpital, clinique, confrère opticien ou audioprothésiste : le même geste pour tous. Osmose prépare le courrier, patient et correspondant déjà renseignés, corrections comprises. Vous relisez, vous adaptez, vous envoyez depuis OhMyVue. Le message part de la boîte de votre établissement et arrive dans la boîte sécurisée du destinataire.",
    li: [
      "Le correspondant se trouve dans l'annuaire santé, sans le chercher",
      "Patient, corrections et date de prescription repris d'Osmose",
      "Un format structuré, repris directement dans leur logiciel métier",
      "Une preuve d'envoi horodatée revient dans votre dossier",
    ],
    link: {
      to: "/message-type",
      label: "Voir le message envoyé à l'ophtalmologiste ou à l'orthoptiste",
    },
    before: "Aujourd'hui : un fax, un courrier, ou un mail dont on ignore le sort.",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 4L3 11l7 3 3 7z" />
        <path d="M21 4l-11 10" />
      </svg>
    ),
  },
  {
    t: "Le dossier médical partagé, en consultation et en alimentation",
    d: "Vous accédez à Mon espace santé et au dossier médical partagé dans les deux sens : vous consultez ce que les autres professionnels y ont déposé, et vous l'alimentez à votre tour. C'est ce qui rend le suivi des soins continu et la coordination entre les 3 O — ophtalmologiste, orthoptiste, opticien — enfin réelle.",
    li: [
      "Consultation : dernière ordonnance, comptes rendus, historique",
      "Alimentation : vos adaptations déposées au moment de l'envoi",
      "Le patient retrouve ses documents dans Mon espace santé",
      "Une trace horodatée de ce que vous avez fait, et quand",
    ],
    before: "Aujourd'hui : chacun garde ses documents dans son coin.",
    link: {
      to: "/dmp",
      label: "Voir comment on consulte et on alimente le dossier",
    },
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 21s7.5-4.2 7.5-10V5.5L12 2.7 4.5 5.5V11c0 5.8 7.5 10 7.5 10z" />
        <path d="M9.2 12l2 2 3.6-3.8" />
      </svg>
    ),
  },
];

const STEPS = [
  {
    n: "01",
    t: "Vous vous connectez",
    d: "Carte CPS ou application e-CPS. Cette seule action vous identifie et identifie votre établissement. Aucun mot de passe.",
  },
  {
    n: "02",
    t: "Vous choisissez votre offre",
    d: "Établissement déjà renseigné, signature électronique, paiement par carte. Contrat et facture suivent.",
  },
  {
    n: "03",
    t: "Nous ouvrons votre boîte",
    d: "Vous déclarez vos professionnels, nous créons la messagerie. L'encart Osmose passe en « Connecté ».",
  },
];

const OFFERS = [
  {
    name: "Messagerie de Santé organisationnelle",
    price: "19,99 €",
    featured: false,
    items: [
      "Votre messagerie sécurisée de santé",
      "Tous vos professionnels rattachés, sans supplément",
      "Envois et réceptions illimités, traçabilité horodatée",
      "Visibilité dans l'annuaire santé",
      "Site vitrine personnalisable inclus",
      "Intégration Osmose incluse",
    ],
  },
  {
    name: "Messagerie + Agenda",
    price: "49,99 €",
    featured: true,
    items: [
      "Tout ce que contient l'offre précédente",
      "Agenda connecté, interopérable avec vos activités",
      "Prise de rendez-vous en ligne",
      "Un agenda par professionnel rattaché",
      "Rappels automatiques aux patients",
    ],
  },
  {
    name: "Messagerie + Agenda + Suivi myopie",
    logo: "/logos/myo-control.jpeg",
    soon: true,
    price: "79,99 €",
    featured: false,
    items: [
      "Tout ce que contient l'offre précédente",
      "Module Myo-Control pour la freination myopique",
      "Passeports et carnets digitalisés",
      "Document unique alimentant le DMP",
    ],
  },
];

const SOCLES = [
  { src: "/logos/insi.png", alt: "INSi — Identité Nationale de Santé" },
  { src: "/logos/mssante-official.png", alt: "MSSanté" },
  { src: "/logos/dmp.png", alt: "Dossier Médical Partagé" },
  { src: "/logos/mon-espace-sante.png", alt: "Mon espace santé" },
  { src: "/logos/psc.png", alt: "Pro Santé Connect" },
  { src: "/logos/hds.png", alt: "Hébergeur de données de santé certifié" },
];

const MORE = [
  { t: "L'agenda et la prise de rendez-vous en ligne", to: "/agenda" },
  { t: "Le site vitrine inclus dans l'offre", to: "/agenda#vitrine" },
  { t: "La MSSanté pour les opticiens", to: "/mssante/opticien" },
  { t: "La MSSanté pour les audioprothésistes", to: "/mssante/audioprothesiste" },
  { t: "L'identité nationale de santé", to: "/services/ins" },
  { t: "Le dossier médical partagé", to: "/services/dmp" },
  { t: "Pro Santé Connect et la carte CPS", to: "/services/psc" },
  { t: "Conformité et hébergement", to: "/conformite" },
];

const Arrow = () => (
  <svg className="omv-arw" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Tick = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

export default function Osmose() {
  return (
    <div className="omv-osm">
      <SEO
        title="Osmose × OhMyVue — vos fonctionnalités e-santé ouvertes depuis Osmose"
        description="Page réservée aux opticiens et audioprothésistes équipés d'Osmose : messagerie sécurisée de santé, agenda connecté et prise de rendez-vous en ligne, espace patient, dossier médical partagé — activés avec votre carte CPS."
        canonical="/osmose"
        noIndex
      />

      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap"
        />
      </Helmet>

      <style>{CSS}</style>

      {/* ---------------- barre haute ---------------- */}
      <header className="omv-topbar">
        <div className="omv-wrap">
          <img className="omv-mark" src="/logos/ohmyvue.png" alt="OhMyVue" />
          <span className="omv-spacer" />
          <a className="omv-btn omv-btn-primary omv-btn-sm" href={CONNEXION_URL}>
            Connexion
          </a>
        </div>
      </header>

      {/* ---------------- hero : le partenariat ---------------- */}
      <div className="omv-hero">
        <div className="omv-wrap">
          <div className="omv-lockup">
            <img className="omv-osmose" src="/logos/osmose.webp" alt="Osmose" />
            <span className="omv-x" aria-hidden="true">
              ×
            </span>
            <img className="omv-omv" src="/logos/ohmyvue.png" alt="OhMyVue" />
            <div className="omv-tag">Partenariat technologique</div>
          </div>

          <h1>
            Toutes vos fonctionnalités e-santé, <em>ouvertes depuis Osmose.</em>
          </h1>
          <p className="omv-lede">
            <b>Messagerie sécurisée de santé</b>, <b>agenda connecté et prise de rendez-vous en
            ligne</b>, <b>espace patient pour le dépôt de documents</b>, <b>consultation et
            alimentation du dossier médical partagé</b> : tout s'active depuis Osmose, sans changer
            de logiciel ni ressaisir quoi que ce soit.
          </p>
          <p className="omv-claim">
            Vous n'ajoutez pas un logiciel de plus. Vous entrez dans le réseau où ophtalmologistes,
            orthoptistes et opticiens se répondent enfin.
          </p>

          <div className="omv-actions">
            <a className="omv-btn omv-btn-primary omv-btn-lg" href={CONNEXION_URL}>
              Connexion <Arrow />
            </a>
            <a className="omv-btn omv-btn-ghost-dark omv-btn-lg" href="#offres">
              Voir les offres
            </a>
          </div>
        </div>
      </div>

      {/* ---------------- bande services socles ---------------- */}
      <div className="omv-strip">
        <div className="omv-wrap">
          <p className="omv-strip-lead">
            OhMyVue est raccordé aux services socles de l'État. L'identité est qualifiée, le message
            transmis, le document déposé.
          </p>
          <div className="omv-marks">
            {SOCLES.map((s) => (
              <img key={s.src} src={s.src} alt={s.alt} />
            ))}
          </div>
        </div>
      </div>

      {/* ---------------- trois bénéfices ---------------- */}
      <section className="omv-sec">
        <div className="omv-wrap">
          <div className="omv-head">
            <span className="omv-pill omv-pill-light">Messagerie sécurisée de santé</span>
            <h2>Une seule boîte, pour tout l'établissement</h2>
            <p>
              Une adresse qui appartient au magasin, pas à une personne. Vos professionnels y sont
              rattachés, tout le monde voit le même flux, et chaque envoi porte le nom de celui qui
              l'a envoyé.
            </p>
          </div>
          <BoiteOrga />
        </div>
      </section>

      <section className="omv-sec">
        <div className="omv-wrap">
          <div className="omv-head">
            <span className="omv-pill omv-pill-light">Ce que ça change</span>
            <h2>Quatre choses qui changent votre journée</h2>
            <p>
              Vous gardez Osmose et vos habitudes. Ce qui change, c'est ce qui entre et ce qui sort
              de votre magasin.
            </p>
          </div>
          <div className="omv-four">
            {POINTS.map((p) => (
              <div className="omv-pt" key={p.t}>
                <div className="omv-ic">{p.icon}</div>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
                <ul>
                  {p.li.map((x) => (
                    <li key={x}>
                      <Tick />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
                {p.link && (
                  <Link className="omv-seelink" to={p.link.to}>
                    {p.link.label} <Arrow />
                  </Link>
                )}
                <span className="omv-before">{p.before}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- démarrer ---------------- */}
      <section className="omv-sec omv-dark">
        <div className="omv-wrap">
          <div className="omv-head">
            <span className="omv-pill omv-pill-dark">Démarrer</span>
            <h2>Trois étapes, votre carte CPS suffit</h2>
          </div>
          <ol className="omv-flow">
            {STEPS.map((s) => (
              <li className="omv-fl" key={s.n}>
                <div className="omv-n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </li>
            ))}
          </ol>
          <p className="omv-flow-note">
            Activation sous 48 heures ouvrées. Vos professionnels sont rattachés sans limite de nombre
            ni supplément.
          </p>
        </div>
      </section>

      {/* ---------------- offres ---------------- */}
      <section className="omv-sec" id="offres">
        <div className="omv-wrap">
          <div className="omv-head">
            <span className="omv-pill omv-pill-light">Tarifs</span>
            <h2>Un prix par établissement</h2>
          </div>
          <div className="omv-offers">
            {OFFERS.map((o) => (
              <div className={"omv-offer" + (o.featured ? " omv-feat" : "") + (o.soon ? " omv-bientot" : "")} key={o.name}>
                {o.featured && <span className="omv-badge">La plus complète</span>}
                {o.soon && <span className="omv-badge omv-badge-soon">Bientôt disponible</span>}
                <div className="omv-oname">{o.name}</div>
                <div className="omv-price">
                  <b>{o.price}</b>
                  <span>/ mois HT</span>
                </div>
                <div className="omv-osub">Par établissement, engagement 12 mois</div>
                {o.logo && (<div className="omv-omodule"><img src={o.logo} alt="Module Myo-Control" /></div>)}
                <ul>
                  {o.items.map((it) => (
                    <li key={it}>
                      <Tick />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                {o.soon && (<div className="omv-onote">Module en cours de finalisation. Activation possible depuis votre espace OhMyVue dès son ouverture.</div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- le choix se fait après la connexion ---------------- */}
      <section className="omv-confirm">
        <div className="omv-wrap">
          <div className="omv-cb-txt">
            <div className="omv-cb-k">Important</div>
            <h2>Le choix de votre offre se fait après la connexion.</h2>
            <p>
              Ces encarts vous présentent le contenu de chaque offre. La sélection et le paiement ont
              lieu sur la plateforme OhMyVue, une fois votre établissement identifié par Pro Santé
              Connect — c'est là que vous choisirez, et nulle part ailleurs.
            </p>
          </div>
          <div className="omv-actions">
            <a className="omv-btn omv-btn-white omv-btn-lg" href={CONNEXION_URL}>
              Connexion <Arrow />
            </a>
            <Link className="omv-btn omv-btn-ghostw omv-btn-lg" to="/demo">
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      
      {/* ---------------- pour aller plus loin ---------------- */}
      <section className="omv-sec omv-more-sec">
        <div className="omv-wrap">
          <div className="omv-head">
            <span className="omv-pill omv-pill-light">Pour aller plus loin</span>
            <h2>Tout comprendre, sur metaconnect.fr</h2>
          </div>
          <div className="omv-more">
            {MORE.map((m) => (
              <Link className="omv-lnk" to={m.to} target="_blank" rel="noopener" key={m.to}>
                <span>
                  <span className="omv-lt">{m.t}</span>
                  <span className="omv-lu">metaconnect.fr{m.to}</span>
                </span>
                <span className="omv-go">
                  <Arrow />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- pied de page ---------------- */}
      <footer className="omv-foot">
        <div className="omv-wrap">
          <div className="omv-op">
            <img src="/logos/ohmyvue.png" alt="OhMyVue" />
            <span>
              Service opéré par MetaConnect.fr, opérateur MSSanté référencé auprès de l'ANS.
            </span>
          </div>
          <div className="omv-links">
            <Link to="/mentions-legales">Mentions légales</Link>
            <Link to="/cgv">CGV</Link>
            <Link to="/cgu">CGU</Link>
            <a href="mailto:contact@metaconnect.fr">contact@metaconnect.fr</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Styles portés par la page. Tout est préfixé .omv-osm : rien ne fuit vers
   le reste du site, et les règles globales sur h1..h6 sont neutralisées.
   Charte reprise de ohmyvue.fr : Instrument Sans, bandes #0F172A / #F5F5F5,
   primaire #0080FF, boutons entièrement arrondis.
   ------------------------------------------------------------------------- */
const CSS = `
.omv-osm{
  --ink:#0F172A; --ink2:#1B2540; --ground:#F5F5F5; --surface:#FFFFFF;
  --line:#E6E8EC; --blue:#0080FF; --blue700:#0064CC; --bsoft:#EFF6FF;
  --bline:#D6E7FB; --text:#0F172A; --muted:#5A6577; --mutedd:#A8B3C7; --ok:#0FA36B;
  background:var(--ground); color:var(--text); min-height:100vh;
  font-family:"Instrument Sans",ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;
  font-size:15.5px; line-height:1.6; -webkit-font-smoothing:antialiased;
}
.omv-osm *{box-sizing:border-box}
.omv-osm img{max-width:100%}
.omv-wrap{max-width:1080px;margin:0 auto;padding:0 28px}
.omv-osm h1,.omv-osm h2,.omv-osm h3{margin:0;text-wrap:balance;color:var(--text);font-weight:600}
.omv-osm h1{font-size:clamp(31px,4vw,44px);line-height:1.12;letter-spacing:-.02em}
.omv-osm h2{font-size:clamp(23px,2.4vw,29px);line-height:1.2;letter-spacing:-.015em}
.omv-osm h3{font-size:16.5px;line-height:1.35}
.omv-osm p{margin:0;color:var(--muted)}
.omv-osm ul,.omv-osm ol{list-style:none;margin:0;padding:0}
.omv-dark{background:var(--ink)}
.omv-dark h2,.omv-dark h3{color:#fff}
.omv-dark p{color:var(--mutedd)}

.omv-btn{display:inline-flex;align-items:center;gap:9px;border-radius:999px;font-weight:600;
  font-size:14px;text-decoration:none;border:1px solid transparent;padding:11px 22px;
  transition:background .15s,border-color .15s;cursor:pointer}
.omv-btn-primary{background:var(--blue);color:#fff}
.omv-btn-primary:hover{background:var(--blue700);color:#fff}
.omv-btn-ghost-dark{border-color:rgba(255,255,255,.24);color:#fff}
.omv-btn-ghost-dark:hover{border-color:rgba(255,255,255,.5);color:#fff}
.omv-btn-white{background:#fff;color:var(--blue);font-size:15.5px;padding:15px 30px}
.omv-btn-white:hover{background:#EAF4FF;color:var(--blue)}
.omv-btn-lg{padding:14px 28px;font-size:15px}
.omv-btn-sm{padding:9px 18px;font-size:13.5px}
.omv-arw{width:15px;height:15px;stroke:currentColor;fill:none;stroke-width:2;
  stroke-linecap:round;stroke-linejoin:round;flex:0 0 15px}

.omv-pill{display:inline-flex;align-items:center;border-radius:999px;font-size:11.5px;
  font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.omv-pill-dark{background:rgba(255,255,255,.09);color:#DCE5F5}
.omv-pill-light{background:#E7ECF3;color:#45526B}

/* barre haute */
.omv-topbar{background:var(--ink)}
.omv-topbar .omv-wrap{display:flex;align-items:center;height:60px}
.omv-mark{height:19px;width:auto;filter:brightness(0) invert(1)}
.omv-spacer{flex:1}

/* hero */
.omv-hero{background:var(--ink);position:relative;overflow:hidden}
.omv-hero::after{content:"";position:absolute;right:-200px;top:-280px;width:720px;height:720px;
  border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(0,128,255,.18) 0%,rgba(0,128,255,0) 62%)}
.omv-hero .omv-wrap{padding:46px 28px 54px;position:relative;z-index:1}
.omv-lockup{display:flex;align-items:center;gap:32px;flex-wrap:wrap;padding-bottom:30px;
  border-bottom:1px solid rgba(255,255,255,.10);margin-bottom:34px}
.omv-osmose{height:132px;width:auto;margin:-14px 0}
.omv-omv{height:38px;width:auto;filter:brightness(0) invert(1)}
.omv-x{width:34px;height:34px;flex:0 0 34px;border-radius:50%;display:grid;place-items:center;
  border:1px solid rgba(255,255,255,.18);color:rgba(255,255,255,.55);font-size:15px}
.omv-tag{margin-left:auto;font-size:11.5px;font-weight:600;letter-spacing:.11em;
  text-transform:uppercase;color:#5FB3FF}
.omv-hero h1{color:#fff;max-width:17ch;margin-bottom:18px}
.omv-hero h1 em{font-style:normal;color:#5FB3FF}
.omv-lede{max-width:54ch}
.omv-hero .omv-lede{font-size:19px;line-height:1.62;color:#AEC1DC}
.omv-hero .omv-lede b{color:#fff;font-weight:600}
.omv-claim{max-width:52ch;margin-top:18px;font-size:16px;font-weight:600;color:#fff;
  padding-left:16px;border-left:2px solid var(--blue);line-height:1.5}
.omv-actions{display:flex;flex-wrap:wrap;gap:12px;margin-top:28px}

/* bande services socles */
.omv-strip{background:var(--surface);border-bottom:1px solid var(--line);padding:26px 0}
.omv-strip .omv-wrap{display:flex;align-items:center;gap:28px;flex-wrap:wrap}
.omv-strip-lead{font-size:13.4px;color:var(--muted);max-width:34ch;flex:1 1 260px;line-height:1.5}
.omv-marks{display:flex;align-items:center;gap:26px;flex-wrap:wrap}
.omv-marks img{height:38px;width:auto;object-fit:contain;opacity:.92}

.omv-sec{padding:66px 0}
.omv-head{max-width:58ch;margin-bottom:32px}
.omv-head h2{margin-top:14px}

/* trois bénéfices */
.omv-three{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.omv-pt{background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:24px 22px}
.omv-ic{width:38px;height:38px;border-radius:11px;display:grid;place-items:center;margin-bottom:15px;
  background:var(--bsoft);border:1px solid var(--bline)}
.omv-ic svg{width:18px;height:18px;stroke:var(--blue);fill:none;stroke-width:1.9;
  stroke-linecap:round;stroke-linejoin:round}
.omv-pt h3{margin-bottom:8px}
.omv-pt p{font-size:13.8px;line-height:1.55}

/* démarrer */
.omv-flow{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.omv-fl{background:var(--ink2);border:1px solid rgba(255,255,255,.09);border-radius:14px;padding:22px 20px}
.omv-n{font-size:12px;font-weight:600;color:#5FB3FF;letter-spacing:.08em;font-variant-numeric:tabular-nums}
.omv-fl h3{margin:9px 0 7px;font-size:15.5px;color:#fff}
.omv-fl p{font-size:13.6px;line-height:1.55;color:var(--mutedd)}
.omv-flow-note{margin-top:18px;font-size:13.4px;color:#8F9DB4}

/* offres */
.omv-offers{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;align-items:stretch}
.omv-offer{background:var(--surface);border:1px solid var(--line);border-radius:18px;
  padding:26px 24px;display:flex;flex-direction:column;position:relative}
.omv-feat{border-color:var(--blue);box-shadow:0 12px 34px -18px rgba(0,128,255,.55)}
.omv-badge{position:absolute;top:-11px;left:24px;background:var(--blue);color:#fff;border-radius:999px;
  font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:5px 13px}
.omv-oname{font-size:15.5px;font-weight:600;line-height:1.3;min-height:2.6em}
.omv-price{display:flex;align-items:baseline;gap:7px;margin-top:14px}
.omv-price b{font-size:33px;font-weight:600;letter-spacing:-.02em;font-variant-numeric:tabular-nums}
.omv-price span{font-size:13.5px;color:var(--muted)}
.omv-osub{font-size:12.4px;color:var(--muted);margin-top:3px}
.omv-omodule{margin-top:16px;padding-top:14px;border-top:1px dashed var(--line)}
.omv-omodule img{height:50px;width:auto;display:block}
.omv-offer ul{margin:20px 0 0;display:grid;gap:9px;flex:1;align-content:start}
.omv-offer li{display:flex;gap:10px;font-size:13.5px;line-height:1.5;color:#3D4759}
.omv-offer li svg{width:14px;height:14px;flex:0 0 14px;margin-top:4px;stroke:var(--ok);fill:none;
  stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}

/* bandeau : le choix se fait après la connexion */
.omv-confirm{background:var(--blue);padding:52px 0}
.omv-confirm .omv-wrap{display:flex;align-items:center;gap:36px;flex-wrap:wrap;
  justify-content:space-between}
.omv-cb-txt{flex:1 1 480px}
.omv-cb-k{font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;
  color:rgba(255,255,255,.72);margin-bottom:12px}
.omv-confirm h2{color:#fff;font-size:clamp(25px,3vw,34px);margin-bottom:14px}
.omv-confirm p{color:rgba(255,255,255,.88);font-size:15.5px;max-width:62ch;line-height:1.6}

/* connexion */
.omv-connect .omv-wrap{display:grid;grid-template-columns:1.05fr .95fr;gap:36px;align-items:center}
.omv-connect h2{margin:16px 0 14px}
.omv-connect p{max-width:50ch}
.omv-sim{background:var(--ink2);border:1px solid rgba(255,255,255,.10);border-radius:18px;padding:26px}
.omv-sim-hd{display:flex;align-items:center;gap:12px;padding-bottom:16px;
  border-bottom:1px solid rgba(255,255,255,.10);margin-bottom:18px}
.omv-sim-hd img{height:17px;width:auto;filter:brightness(0) invert(1)}
.omv-sim-hd span{font-size:12.5px;color:#8F9DB4;margin-left:auto}
.omv-sim-grid{display:grid;gap:15px}
.omv-lab{font-size:11.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#7F8DA5}
.omv-val{font-size:15px;font-weight:600;color:#fff;margin-top:4px}
.omv-sim-psc{margin-top:19px;display:flex;align-items:center;gap:12px;
  background:rgba(0,128,255,.12);border:1px solid rgba(0,128,255,.32);border-radius:12px;padding:14px 16px}
.omv-sim-psc svg{width:19px;height:19px;flex:0 0 19px;stroke:#5FB3FF;fill:none;stroke-width:1.9;
  stroke-linecap:round;stroke-linejoin:round}
.omv-sim-psc div{font-size:13.4px;color:#CFDCEF;line-height:1.45}

/* pour aller plus loin */
.omv-more-sec{background:var(--surface);border-top:1px solid var(--line)}
.omv-more{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
.omv-lnk{display:flex;align-items:center;gap:14px;background:var(--ground);
  border:1px solid var(--line);border-radius:13px;padding:16px 18px;text-decoration:none;
  color:var(--text);transition:border-color .15s,transform .15s}
.omv-lnk:hover{border-color:var(--blue);transform:translateY(-1px);color:var(--text)}
.omv-lt{display:block;font-size:14.2px;font-weight:600;line-height:1.3}
.omv-lu{display:block;font-size:12.4px;color:var(--muted);margin-top:2px}
.omv-go{margin-left:auto;color:var(--blue);display:grid;place-items:center;flex:0 0 auto}

/* pied de page */
.omv-foot{background:#0A1120;color:#8C99AD;padding:34px 0;font-size:13px}
.omv-foot .omv-wrap{display:flex;flex-wrap:wrap;gap:18px;align-items:center;
  justify-content:space-between}
.omv-op{display:flex;align-items:center;gap:11px;max-width:58ch}
.omv-op img{height:17px;width:auto;opacity:.75;filter:brightness(0) invert(1)}
.omv-links{display:flex;gap:18px;flex-wrap:wrap}
.omv-foot a{color:#B7C3D8;text-decoration:none}
.omv-foot a:hover{text-decoration:underline;color:#fff}

@media (max-width:900px){
  .omv-three,.omv-flow,.omv-offers,.omv-more{grid-template-columns:1fr}
  .omv-connect .omv-wrap{grid-template-columns:1fr}
  .omv-osmose{height:96px;margin:-10px 0}
  .omv-tag{margin-left:0;width:100%}
  .omv-marks{gap:20px}
  .omv-marks img{height:26px}
}
@media (prefers-reduced-motion:reduce){.omv-osm *{transition:none !important}}
.omv-osm a:focus-visible,.omv-osm button:focus-visible{outline:2px solid var(--blue);
  outline-offset:3px;border-radius:6px}

/* ---- offre en preparation ---- */
.omv-bientot{background:#FBFBFC;border-style:dashed;border-color:#D8DEE8}
.omv-bientot .omv-oname,.omv-bientot .omv-price b{color:#59637A}
.omv-bientot li{color:#6C7588}.omv-bientot li svg{stroke:#9AA6B8}
.omv-bientot .omv-omodule img{opacity:.9}
.omv-badge-soon{background:#E7ECF3;color:#45526B;border:1px solid #D2DAE6}
.omv-onote{margin-top:16px;padding-top:13px;border-top:1px dashed var(--line);
  font-size:12.6px;line-height:1.5;color:#6C7588;text-align:center}

/* ---- composition centree ---- */
.omv-hero::after{right:auto;left:50%;transform:translateX(-50%);top:-380px;width:940px;height:940px}
.omv-hero .omv-wrap{text-align:center;padding:54px 28px 62px}
.omv-lockup{justify-content:center;gap:30px;padding-bottom:28px;margin-bottom:30px}
.omv-tag{order:-1;flex:0 0 100%;margin-left:0;text-align:center;margin-bottom:2px}
.omv-hero h1{max-width:19ch;margin-left:auto;margin-right:auto}
.omv-lede{max-width:60ch;margin-left:auto;margin-right:auto}
.omv-claim{max-width:56ch;margin:22px auto 0;padding:15px 22px;border-left:0;border-radius:14px;
  background:rgba(0,128,255,.10);border:1px solid rgba(95,179,255,.30);text-align:center}
.omv-actions{justify-content:center;margin-top:30px}
.omv-strip .omv-wrap{flex-direction:column;text-align:center;gap:18px}
.omv-strip-lead{flex:none;max-width:62ch}
.omv-marks{justify-content:center}
.omv-head{max-width:62ch;margin-left:auto;margin-right:auto;text-align:center}
.omv-pt{text-align:center}
.omv-ic{margin-left:auto;margin-right:auto}
.omv-fl{text-align:center}
.omv-flow-note{text-align:center}
.omv-oname,.omv-osub{text-align:center}
.omv-price{justify-content:center}
.omv-omodule img{margin-left:auto;margin-right:auto}
.omv-badge{left:50%;transform:translateX(-50%)}
.omv-confirm .omv-wrap{flex-direction:column;justify-content:center;text-align:center;gap:26px}
.omv-cb-txt{flex:none;max-width:70ch}
.omv-confirm p{margin-left:auto;margin-right:auto}
.omv-connect .omv-wrap{grid-template-columns:1fr;justify-items:center;text-align:center;gap:32px}
.omv-connect p{margin-left:auto;margin-right:auto}
.omv-sim{width:100%;max-width:520px;text-align:left}
.omv-foot .omv-wrap{justify-content:center;text-align:center;flex-direction:column;gap:14px}
.omv-op,.omv-links{justify-content:center}

/* ---- connexion : panneau ---- */
.omv-connect{position:relative;overflow:hidden}
.omv-connect::before{content:"";position:absolute;left:50%;top:34%;transform:translateX(-50%);
  width:860px;height:540px;border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(0,128,255,.17) 0%,rgba(0,128,255,0) 66%)}
.omv-connect .omv-wrap{position:relative;z-index:1;gap:34px}
.omv-sim{max-width:660px;padding:0;overflow:hidden;border-radius:20px;
  background:linear-gradient(180deg,#17223C 0%,#101A2F 100%);
  border:1px solid rgba(255,255,255,.14);
  box-shadow:0 34px 72px -34px rgba(0,110,235,.55)}
.omv-sim-hd{margin:0;padding:15px 22px;background:rgba(255,255,255,.045);
  border-bottom:1px solid rgba(255,255,255,.09)}
.omv-sim-grid{gap:0;padding:4px 22px}
.omv-sim-grid>div{display:flex;align-items:baseline;justify-content:space-between;gap:20px;
  padding:14px 0;border-bottom:1px solid rgba(255,255,255,.07)}
.omv-sim-grid>div:last-child{border-bottom:0}
.omv-lab{flex:0 0 auto}
.omv-val{margin-top:0;text-align:right;font-size:14px}
.omv-sim-psc{margin:2px 22px 22px}
.omv-btn-ghostw{border-color:rgba(255,255,255,.55);color:#fff}
.omv-btn-ghostw:hover{border-color:#fff;color:#fff}
.omv-osm .omv-lede{margin-left:auto;margin-right:auto}
.omv-osm .omv-claim{margin:22px auto 0}
.omv-osm .omv-strip-lead{margin-left:auto;margin-right:auto}
.omv-osm .omv-claim{color:#fff;font-weight:600}

/* ---- quatre encarts ---- */
.omv-four{display:grid;grid-template-columns:repeat(2,1fr);gap:18px;align-items:stretch}
.omv-osm .omv-pt{display:flex;flex-direction:column;padding:28px 26px;text-align:center}
.omv-osm .omv-pt h3{font-size:18px;line-height:1.3;margin-bottom:10px}
.omv-osm .omv-pt p{font-size:14px;line-height:1.6;max-width:52ch;margin-left:auto;margin-right:auto}
.omv-osm .omv-head p{margin-top:12px;font-size:15.5px}
.omv-osm .omv-pt ul{margin-top:18px;padding-top:16px;border-top:1px solid var(--line);
  display:grid;gap:10px;text-align:left;flex:1;align-content:start}
.omv-osm .omv-pt li{display:flex;gap:10px;font-size:13.6px;line-height:1.5;color:#3D4759}
.omv-osm .omv-pt li svg{width:14px;height:14px;flex:0 0 14px;margin-top:4px;stroke:var(--ok);
  fill:none;stroke-width:2.6;stroke-linecap:round;stroke-linejoin:round}
.omv-seelink{display:inline-flex;align-items:center;gap:8px;margin-top:16px;align-self:center;
  font-size:13.6px;font-weight:600;color:var(--blue);text-decoration:none;
  border:1px solid var(--bline);background:var(--bsoft);border-radius:999px;padding:9px 17px}
.omv-seelink:hover{background:#DCEDFF;color:var(--blue)}
.omv-before{display:block;margin-top:15px;padding-top:12px;border-top:1px dashed var(--line);
  font-size:12.6px;color:#7C8698;font-style:italic;text-align:left}
@media (max-width:900px){.omv-four{grid-template-columns:1fr}}
`;
